import Rollup, { RollupType } from './Rollup';
import onFocusedOtherRollupClicked from '../utils/onFocusedOtherRollupClicked';
import shouldShowFocusedRollup from '../utils/shouldShowFocusedRollup';
import shouldShowRollup from '../utils/shouldShowRollup';
import { observer } from 'owa-mobx-react';
import type { TableView } from 'owa-mail-list-store';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import React from 'react';
import {
    getFocusedInboxRollupStore,
    getRollupText,
    updateWatermarksAndResetRollup,
} from 'owa-mail-focused-inbox-rollup-store';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { shouldRenderColumnHeaders, isSingleLineListView } from 'owa-mail-layout';
import {
    rollupTextSize14,
    rollupTextSize12,
    focusedOtherRollupContainer,
    focusedOtherDismissButton,
    focusedOtherPrimaryText,
    focusedOtherSecondaryText,
    focusedOtherRollupPinned,
    focusedOtherRollupPinnedUnderColumnHeaders,
    focusedOtherPrimaryTextMCL,
    focusedOtherDismissButtonIcon,
    focusedOtherRollupContainerDenseless,
} from './RollupContainer.scss';
import classnames from 'owa-classnames';
import { isFeatureEnabled } from 'owa-feature-flags';

export interface RollupContainerProps {
    focusedViewFilter: FocusedViewFilter;
    tableView: TableView;
    currentRowKey?: string; // Unnecessary when not in legacy inline placement
    previousRowKey?: string | null; // Unnecessary when not in legacy inline placement
    shouldShowRollupOverride?: boolean; // Used to override the logic to show rollup (when not in legacy inline placement)
}

const defaultExport: React.FunctionComponent<RollupContainerProps> = observer(
    function RollupContainer(props: RollupContainerProps) {
        const {
            currentRowKey = '',
            focusedViewFilter,
            previousRowKey = '',
            tableView,
            shouldShowRollupOverride = false,
        } = props;

        const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');

        const isMCL = isSingleLineListView();

        const onFocusedOtherRollupDismissed = React.useCallback(() => {
            updateWatermarksAndResetRollup(
                focusedViewFilter,
                'ExitRollupButton',
                getMailboxInfo(tableView)
            );
        }, [focusedViewFilter, tableView]);

        const rollupStyles = useComputedValue(() => {
            const primaryTextStyle = isMCL
                ? getDensityModeCssClass(rollupTextSize14, rollupTextSize12, rollupTextSize12)
                : getDensityModeCssClass(rollupTextSize14, rollupTextSize14, rollupTextSize12);

            const pinnedContainerStlyes = shouldRenderColumnHeaders(tableView.id)
                ? focusedOtherRollupPinnedUnderColumnHeaders
                : focusedOtherRollupPinned;

            const containerClassName = isRPDenselessEnabled
                ? focusedOtherRollupContainerDenseless
                : focusedOtherRollupContainer;

            return {
                containerClassName: classnames(containerClassName, pinnedContainerStlyes),
                dismissButtonClassName: focusedOtherDismissButton,
                dismissButtonIconClassName: focusedOtherDismissButtonIcon,
                primaryTextClassName: classnames(
                    isMCL ? focusedOtherPrimaryTextMCL : focusedOtherPrimaryText,
                    primaryTextStyle
                ),
                secondaryTextClassName: classnames(
                    focusedOtherSecondaryText,
                    getDensityModeCssClass(rollupTextSize14, rollupTextSize12, rollupTextSize12)
                ),
            };
        }, [tableView.id, isMCL, isRPDenselessEnabled]);

        // Check if we should show any rollup. If not, just early return.
        if (!shouldShowRollupOverride && !shouldShowRollup(previousRowKey, currentRowKey)) {
            return null;
        }

        // Only evaluate if we determine either F/O rollup should be shown.
        let showFocusedRollup = false;
        let showOtherRollup = false;
        if (shouldShowFocusedRollup()) {
            // Show if we're in Other pivot.
            showFocusedRollup = focusedViewFilter == 1;

            // Show if we're in Focused pivot.
            showOtherRollup = focusedViewFilter == 0;
        }

        if (showFocusedRollup || showOtherRollup) {
            const mailboxInfo = getMailboxInfo(tableView);
            const focusedInboxRollupStore = getFocusedInboxRollupStore(mailboxInfo);

            return (
                <Rollup
                    onClick={onFocusedOtherRollupClicked}
                    onDismissed={onFocusedOtherRollupDismissed}
                    primaryText={getRollupText(focusedInboxRollupStore.viewType, mailboxInfo)}
                    secondaryText={focusedInboxRollupStore.uniqueSenders}
                    styles={rollupStyles}
                    tableView={tableView}
                    rollupType={RollupType.FocusedOther}
                />
            );
        }

        return null;
    },
    'RollupContainer'
);
export default defaultExport;
